// ABOUTME: HTTP API client for Obsidian BI backend communication.
// ABOUTME: Handles authentication, error handling, and request/response formatting.

import type {
  InvestigationDetailResponse,
  InvestigationListResponse,
  CreateInvestigationRequest,
  CreateInvestigationResponse,
  SendMessageRequest,
  SendMessageResponse,
  DataSource,
  TestDataSourceRequest,
  TestDataSourceResponse,
  CreateDataSourceRequest,
  APIError,
  PaginationParams,
  UserProfile,
  UserPreferences,
  UpdateUserPreferencesRequest,
  WorkspaceMetrics,
  OnboardingProgress,
  UpdateOnboardingProgressRequest,
  FeatureFlags,
  DomainPeersResponse,
  DismissDomainPromptResponse,
  DomainClustersResponse,
} from '@/types/api';

// API configuration
export const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

// Custom error class for API errors
export class ApiError extends Error {
  constructor(
    public status: number,
    public error: string,
    message: string,
    public details?: unknown,
    public requestId?: string,
    public retryAfter?: number
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

// Token getter function (set by AuthContext)
// Exported for use by XHR-based upload helpers that can't use apiFetch
export let getToken: (() => Promise<string>) | null = null;

// Workspace ID getter function (set by AppContext)
// Exported for use by XHR-based upload helpers that can't use apiFetch
export let getWorkspaceId: (() => string) | null = null;

export function setTokenGetter(getter: () => Promise<string>) {
  getToken = getter;
}

export function setWorkspaceIdGetter(getter: () => string) {
  getWorkspaceId = getter;
}

// Base fetch wrapper with auth and error handling
export async function apiFetch<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const url = `${API_BASE_URL}${endpoint}`;

  // Get auth token
  let token: string | null = null;
  if (getToken) {
    try {
      token = await getToken();
    } catch {
      // Token acquisition failed — request will proceed unauthenticated
    }
  }

  // Get current workspace ID
  let workspaceId: string | null = null;
  if (getWorkspaceId) {
    workspaceId = getWorkspaceId();
  }

  // Build headers
  // Don't send X-Workspace-ID to /users/me endpoint (user info is workspace-independent)
  // Don't set Content-Type for FormData — the browser sets it with the boundary
  const isUserMeEndpoint = /\/users\/me(\/|$|\?)/.test(url);
  const isFormData = options.body instanceof FormData;
  const headers: HeadersInit = {
    ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(!isUserMeEndpoint && workspaceId && workspaceId !== 'ws_default' ? { 'X-Workspace-ID': workspaceId } : {}),
    ...(options.headers || {}),
  };

  const response = await fetch(url, {
    ...options,
    headers,
  });

  // Handle non-OK responses
  if (!response.ok) {
    let errorData: APIError;
    try {
      errorData = await response.json();
    } catch {
      errorData = {
        error: 'unknown_error',
        message: `HTTP ${response.status}: ${response.statusText}`,
      };
    }

    // Handle 403 workspace access errors - reset to primary workspace
    if (response.status === 403 && errorData.detail?.includes('workspace')) {
      console.error('[API Client] Workspace access denied, resetting to primary');
      // Dispatch custom event to trigger workspace reset
      window.dispatchEvent(new CustomEvent('workspace-access-denied'));
    }

    throw new ApiError(
      response.status,
      errorData.error || 'api_error',
      errorData.message || errorData.detail || `HTTP ${response.status}: ${response.statusText}`,
      errorData.details,
      errorData.request_id,
      errorData.retry_after
    );
  }

  // Handle empty responses
  if (response.status === 204) {
    return undefined as T;
  }

  return response.json();
}

// Investigation API
export const investigationApi = {
  list: (params?: PaginationParams): Promise<InvestigationListResponse> => {
    const query = new URLSearchParams();
    if (params?.limit) query.set('limit', String(params.limit));
    if (params?.offset) query.set('offset', String(params.offset));
    if (params?.search) query.set('search', params.search);
    const queryString = query.toString();
    return apiFetch(`/investigations${queryString ? `?${queryString}` : ''}`);
  },

  get: (id: string): Promise<InvestigationDetailResponse> => {
    return apiFetch(`/investigations/${id}`);
  },

  create: (data: CreateInvestigationRequest): Promise<CreateInvestigationResponse> => {
    return apiFetch('/investigations', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  sendMessage: (id: string, data: SendMessageRequest): Promise<SendMessageResponse> => {
    return apiFetch(`/investigations/${id}/messages`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  delete: (id: string): Promise<void> => {
    return apiFetch(`/investigations/${id}`, {
      method: 'DELETE',
    });
  },

  rename: (id: string, title: string): Promise<void> => {
    return apiFetch(`/investigations/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ title }),
    });
  },

  getSuggestedPrompts: (id: string): Promise<{
    prompts: string[];
    data_source_id: string | null;
    data_source_name: string | null;
  }> => {
    return apiFetch(`/investigations/${id}/suggested-prompts`);
  },

  getWorkspaceSuggestedPrompts: (): Promise<{
    prompts: string[];
    data_source_id: string | null;
    data_source_name: string | null;
  }> => {
    return apiFetch('/investigations/suggested-prompts');
  },
};

// Data Source API
export const dataSourceApi = {
  list: (): Promise<DataSource[]> => {
    return apiFetch('/data-sources');
  },

  get: (id: string): Promise<DataSource> => {
    return apiFetch(`/data-sources/${id}`);
  },

  create: (data: CreateDataSourceRequest): Promise<DataSource> => {
    return apiFetch('/data-sources', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  test: (data: TestDataSourceRequest): Promise<TestDataSourceResponse> => {
    return apiFetch('/data-sources/test', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  delete: (id: string): Promise<void> => {
    return apiFetch(`/data-sources/${id}`, {
      method: 'DELETE',
    });
  },
};

// Admin API
export const adminApi = {
  listWorkspaces: (params?: PaginationParams) => {
    const query = new URLSearchParams();
    if (params?.limit) query.set('limit', String(params.limit));
    if (params?.offset) query.set('offset', String(params.offset));
    const queryString = query.toString();
    return apiFetch(`/admin/workspaces${queryString ? `?${queryString}` : ''}`);
  },

  getWorkspaceUsage: (id: string) => {
    return apiFetch(`/admin/workspaces/${id}/usage`);
  },

  impersonate: (userId: string) => {
    return apiFetch('/admin/impersonate', {
      method: 'POST',
      body: JSON.stringify({ user_id: userId }),
    });
  },

  getAgentConfig: (workspaceId: string) => {
    return apiFetch(`/admin/workspaces/${workspaceId}/agent-config`);
  },

  updateAgentConfig: (workspaceId: string, config: unknown) => {
    return apiFetch(`/admin/workspaces/${workspaceId}/agent-config`, {
      method: 'PUT',
      body: JSON.stringify(config),
    });
  },

  getDomainClusters: (params?: {
    min_users?: number;
    search?: string;
    include_consumer?: boolean;
  }): Promise<DomainClustersResponse> => {
    const query = new URLSearchParams();
    if (params?.min_users) query.set('min_users', String(params.min_users));
    if (params?.search) query.set('search', params.search);
    if (params?.include_consumer) query.set('include_consumer', 'true');
    const queryString = query.toString();
    return apiFetch(`/admin/domain-clusters${queryString ? `?${queryString}` : ''}`);
  },
};

// User API
export const userApi = {
  me: (): Promise<UserProfile> => {
    return apiFetch('/users/me');
  },

  updatePreferences: (data: UpdateUserPreferencesRequest): Promise<UserPreferences> => {
    return apiFetch('/users/me/preferences', {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  updateProfile: (data: { name?: string }): Promise<{ id: string; email: string; name: string; avatar_url: string | null }> => {
    return apiFetch('/users/me/profile', {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  updateOnboardingProgress: (data: UpdateOnboardingProgressRequest): Promise<OnboardingProgress> => {
    return apiFetch('/users/me/onboarding', {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  getSpecializedContent: (): Promise<{ content: string }> => {
    return apiFetch('/users/me/specialized-content');
  },

  setSpecializedContent: (content: string): Promise<{ content: string }> => {
    return apiFetch('/users/me/specialized-content', {
      method: 'PUT',
      body: JSON.stringify({ content }),
    });
  },

  parseSpecializedContentPdf: (file: File): Promise<{ content: string }> => {
    const form = new FormData();
    form.append('file', file);
    return apiFetch('/users/me/specialized-content/parse-pdf', {
      method: 'POST',
      body: form,
    });
  },

  getDomainPeers: (): Promise<DomainPeersResponse> => {
    return apiFetch('/users/me/domain-peers');
  },

  dismissDomainPrompt: (): Promise<DismissDomainPromptResponse> => {
    return apiFetch('/users/me/dismiss-domain-prompt', {
      method: 'POST',
    });
  },
};

// Workspace Metrics API
export const workspaceMetricsApi = {
  get: (): Promise<WorkspaceMetrics> => {
    return apiFetch('/workspace-metrics');
  },
};

// Features API (public — no auth required)
export const featuresApi = {
  getFlags: async (): Promise<FeatureFlags> => {
    const response = await fetch(`${API_BASE_URL}/features`);
    if (!response.ok) throw new Error('Failed to fetch feature flags');
    return response.json();
  },
};

// Export all APIs
export const api = {
  investigations: investigationApi,
  dataSources: dataSourceApi,
  admin: adminApi,
  users: userApi,
  workspaceMetrics: workspaceMetricsApi,
  features: featuresApi,
};
